import React, { useState, useMemo } from 'react';
import { Clock, AlertTriangle, X, Users } from 'lucide-react';

/**
 * TrialCountdownBanner — Persistent Home dashboard banner showing trial days remaining
 * with loss-aversion messaging that intensifies as expiry approaches.
 * Includes fake social proof stats for conversion FOMO.
 * 
 * Messaging tiers:
 * - 5-7 days: Encouraging, low urgency
 * - 3-4 days: Medium urgency, mention what they've built
 * - 1-2 days: High urgency, specific loss callout
 * - 0 days: Expired state, recovery CTA
 */

// Fake social proof — rotates per session, feels alive
const SOCIAL_PROOF_LINES = [
  { text: '92% of users who upgraded kept their MIQ gains', icon: null },
  { text: '2,847 meals analyzed by SmartPlAIte today', icon: null },
  { text: '73% of trial users upgraded before day 5', icon: null },
  { text: '4,112 AI insights generated this week', icon: null },
  { text: 'Users who upgrade lose 2.3x more weight on average', icon: null },
  { text: '1,936 SmartGoals hit by users this month', icon: null },
];

const TrialCountdownBanner = ({
  daysRemaining,
  isTrial,
  miqScore,
  totalMealsLogged,
  onUpgrade
}) => {
  const [dismissed, setDismissed] = useState(false);

  // Pick a stable social proof line per session (changes on page refresh)
  const socialProof = useMemo(() => {
    const idx = Math.floor(Math.random() * SOCIAL_PROOF_LINES.length);
    return SOCIAL_PROOF_LINES[idx];
  }, []);

  // Only show for active trial users with days remaining
  if (!isTrial || dismissed) return null;

  // Determine urgency tier
  const isUrgent = daysRemaining <= 2;
  const isMedium = daysRemaining <= 4 && daysRemaining > 2;
  const isExpired = daysRemaining <= 0;

  // Build personalized loss message
  const getLossItems = () => {
    const items = [];
    if (miqScore) items.push(`MIQ score of ${miqScore}`);
    if (totalMealsLogged > 0) items.push(`${totalMealsLogged} logged meal${totalMealsLogged > 1 ? 's' : ''}`);
    items.push('AI-powered insights');
    return items;
  };

  const getMessage = () => {
    if (isExpired) {
      return {
        text: 'Your trial ended',
        sub: `Upgrade to restore your ${getLossItems().slice(0, 2).join(' and ')}`,
        cta: 'Restore Access'
      };
    }
    if (isUrgent) {
      const lossText = getLossItems().slice(0, 2).join(', ');
      return {
        text: `${daysRemaining} day${daysRemaining > 1 ? 's' : ''} left`,
        sub: `Your ${lossText} will be locked`,
        cta: 'Keep My Progress'
      };
    }
    if (isMedium) {
      return {
        text: `${daysRemaining} days left on your trial`,
        sub: miqScore ? `Your MetaBolicIQ is at ${miqScore} and learning` : 'Your AI is still calibrating to you',
        cta: 'Upgrade Now'
      };
    }
    return {
      text: `${daysRemaining} days left on your trial`,
      sub: 'Every meal you log makes your AI smarter',
      cta: 'View Plans'
    };
  };

  const msg = getMessage();

  const bgColor = isExpired
    ? 'rgba(239, 68, 68, 0.1)'
    : isUrgent
      ? 'rgba(251, 146, 60, 0.1)'
      : 'rgba(102, 126, 234, 0.08)';

  const borderColor = isExpired
    ? 'rgba(239, 68, 68, 0.3)'
    : isUrgent
      ? 'rgba(251, 146, 60, 0.3)'
      : 'rgba(102, 126, 234, 0.2)';

  const accentColor = isExpired ? '#ef4444' : isUrgent ? '#fb923c' : '#667eea';

  const IconComponent = isUrgent || isExpired ? AlertTriangle : Clock;

  return (
    <div
      data-testid="trial-countdown-banner"
      style={{
        margin: '0 0 12px 0',
        borderRadius: '12px',
        background: bgColor,
        border: `1px solid ${borderColor}`,
        overflow: 'hidden'
      }}
    >
      {/* Main banner row */}
      <div style={{
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <IconComponent size={18} color={accentColor} style={{ flexShrink: 0 }} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '13px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            {msg.text}
            {isUrgent && !isExpired && (
              <span style={{
                fontSize: '10px',
                padding: '2px 6px',
                borderRadius: '4px',
                background: accentColor,
                color: 'white',
                fontWeight: '700'
              }}>
                {daysRemaining === 1 ? 'LAST DAY' : 'ENDING SOON'}
              </span>
            )}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
            {msg.sub}
          </div>
        </div>

        <button
          data-testid="trial-upgrade-cta"
          onClick={onUpgrade}
          style={{
            padding: '8px 14px',
            fontSize: '12px',
            fontWeight: '600',
            background: accentColor,
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
        >
          {msg.cta}
        </button>

        {!isUrgent && !isExpired && (
          <button
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
            style={{
              background: 'none',
              border: 'none',
              padding: '4px',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              opacity: 0.4,
              flexShrink: 0
            }}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Social proof strip */}
      <div
        data-testid="social-proof-strip"
        style={{
          padding: '6px 14px 8px',
          borderTop: `1px solid ${borderColor}`,
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}
      >
        <Users size={11} color="var(--text-secondary)" style={{ opacity: 0.6, flexShrink: 0 }} />
        <span style={{
          fontSize: '10px',
          color: 'var(--text-secondary)',
          opacity: 0.7
        }}>
          {socialProof.text}
        </span>
      </div>
    </div>
  );
};

export default TrialCountdownBanner;
